// Purchase Order — daftar (Phase 1E-δ #2)
//
// Alur PO sejak redesign Fase 2b:
//   PR disetujui → PO LAHIR OTOMATIS (PENDING_VENDOR) → Admin Purchasing
//   menetapkan vendor + harga → ISSUED → PARTIAL_RECEIVED →
//   FULLY_RECEIVED → COMPLETED (otomatis, kalau lunas) / CLOSED (manual).
// Tidak ada tombol "Buat PO" di mana pun — PO tidak pernah dibuat tangan.
//
// NOL KONSTANTA BISNIS DI FILE INI. Daftar status dan wewenang role dibaca
// dari GET /purchasing/meta/po. Yang ada di sini hanya pemetaan warna
// (presentasi murni) yang jatuh ke netral untuk kode yang belum dikenal —
// jadi status baru di backend tampil apa adanya, bukan hilang.
import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Card } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { StatusBadge, toneFromMeta } from "../../components/etams/StatusBadge";
import { PackageSearch, RefreshCw, ArrowRight, AlertCircle } from "lucide-react";
import { toast } from "sonner";
import { formatIDR, formatDate } from "../../lib/format";
import { useStoreTimezones } from "../../lib/timezone";
import { listPOs, getPOMeta, apiErrorMessage } from "../../lib/purchasingApi";
import VendorTelatPanel from "./VendorTelatPanel";
import { useDaftar } from "../../hooks/useDaftar";
import { PalangDaftar, ThUrut, Paginasi } from "../../components/etams/PalangDaftar";
import {
  BELUM_SELESAI, paramStatus, saringanStatus, saringanDari, saringanSampai,
} from "../../lib/saringanPurchasing";

//: Cermin `URUT_SAH_PO` di backend/services/po_service.py.
const URUT_SAH = ["po_date", "po_number", "status", "total_amount", "created_at"];

//: Tone dikirim backend per status; terjemahan tone → warna hidup di satu
//: tempat: components/etams/StatusBadge (Q68). Backend menambah status baru
//: → tone-nya sudah ikut, file ini tidak berubah.
export function statusBadge(meta, code) {
  const s = (meta?.statuses || []).find(x => x.code === code);
  return {
    label: s?.label || code || "—",
    description: s?.description || "",
    tone: toneFromMeta(s?.tone),
  };
}

export default function PurchaseOrders() {
  const navigate = useNavigate();
  const { tzForRegion } = useStoreTimezones();

  const [meta, setMeta] = useState(null);
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [pendingCount, setPendingCount] = useState(null);
  const [loading, setLoading] = useState(true);

  // POLA DAFTAR SERAGAM (28 Sep 2026; docs/POLA_DAFTAR.md). Saringan bawaan
  // "Belum selesai" (tanpa Selesai/Ditutup/Batal) — TERLIHAT sebagai chip dan bisa
  // dihapus. Cari/urut/halaman/saringan dikerjakan server dan tersimpan di alamat.
  const daftar = useDaftar(items, {
    server: { total, urutSah: URUT_SAH },
    urutAwal: { kunci: "po_date", arah: "desc" },
    saringan: {
      status: saringanStatus(meta),
      dari: saringanDari(),
      sampai: saringanSampai(),
    },
  });
  const { cari, limit, offset, sort, arah } = daftar.paramServer;
  const { paramSaringan } = daftar;
  const statusFilter = daftar.nilaiSaringan.status;

  const bangunParams = useCallback((ovr = {}) => ({
    ...paramStatus(paramSaringan),
    ...(cari && { q: cari }),
    limit, offset, sort, arah, ...ovr,
  }), [paramSaringan, cari, limit, offset, sort, arah]);

  const ambilHalaman = useCallback(async (off, n) =>
    (await listPOs(bangunParams({ offset: off, limit: n }))).data, [bangunParams]);

  useEffect(() => {
    getPOMeta()
      .then(r => setMeta(r.data))
      .catch(e => toast.error(apiErrorMessage(e, "Gagal memuat info status PO.")));
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await listPOs(bangunParams());
      setItems(r.data.items || []);
      setTotal(r.data.total || 0);
    } catch (e) {
      toast.error(apiErrorMessage(e, "Gagal memuat daftar PO."));
    } finally {
      setLoading(false);
    }
  }, [bangunParams]);

  useEffect(() => { load(); }, [load]);

  // Jumlah PO yang menunggu vendor — diambil dari `total` hasil filter di
  // backend, BUKAN dihitung dari halaman yang sedang tampil. Kalau dihitung
  // di UI, angkanya hanya benar di halaman 1 dan diam-diam salah setelahnya.
  const refreshPending = useCallback(() => {
    listPOs({ status: "PENDING_VENDOR", page: 1, page_size: 1 })
      .then(r => setPendingCount(r.data.total || 0))
      .catch((e) => { setPendingCount(null); toast.error(apiErrorMessage(e, "Data gagal dimuat.")); });
  }, []);
  // SEKALI per buka layar (+ tombol Refresh) — dulu ikut `items` sehingga terpanggil dua kali saat
  // layar dibuka (items [] → items termuat); DAFTAR-SERAGAM-SISA-KECIL-01 butir 1.
  useEffect(() => { refreshPending(); }, [refreshPending]);

  const showingPending = statusFilter === "PENDING_VENDOR";
  const adaSaringan = !!daftar.cari.trim() || daftar.saringanAktif.length > 0;
  const KOLOM_CSV = [
    { kunci: "po_number", judul: "Nomor PO" },
    { kunci: "po_date", judul: "Tanggal", ambil: (po) => formatDate(po.po_date) },
    { kunci: "vendor", judul: "Vendor", ambil: (po) => po.vendor?.name || "" },
    { kunci: "pr", judul: "Dari PR", ambil: (po) => po.pr?.pr_number || "" },
    { kunci: "region", judul: "Wilayah", ambil: (po) => po.region?.name || "" },
    { kunci: "total_amount", judul: "Nilai" },
    { kunci: "status", judul: "Status", ambil: (po) => statusBadge(meta, po.status).label },
  ];

  return (
    <div className="space-y-6" data-testid="po-page">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h1 className="text-2xl font-semibold flex items-center gap-2">
            <PackageSearch className="h-6 w-6" /> Purchase Order
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            PO lahir otomatis begitu PR disetujui — tidak dibuat manual.
            Langkah berikutnya menetapkan vendor dan harga.
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={() => { load(); refreshPending(); }}
                data-testid="po-refresh">
          <RefreshCw className="h-4 w-4 mr-1" /> Refresh
        </Button>
      </div>

      {/* Pekerjaan yang menunggu — sengaja di atas daftar, bukan sebagai
          baris yang tenggelam di antara 40 PO lain. */}
      {pendingCount > 0 && !showingPending && (
        <button type="button" onClick={() => daftar.setSaringan("status", "PENDING_VENDOR")}
                data-testid="po-pending-banner"
                className="w-full text-left bg-warning-soft text-warning-soft-foreground border border-warning/40 rounded p-3
                           hover:bg-warning-soft/70 transition flex items-center gap-3">
          <AlertCircle className="h-5 w-5 shrink-0" />
          <span className="text-sm">
            <strong>{pendingCount} PO menunggu vendor.</strong>{" "}
            Barang belum bisa diterima sebelum vendor dan harganya ditetapkan.
          </span>
          <ArrowRight className="h-4 w-4 ml-auto shrink-0" />
        </button>
      )}

      {/* BUSINESS-53: vendor telat (mulai H+1 janji kirim) + keterlambatan per vendor. */}
      {meta?.permissions?.baca_telat && <VendorTelatPanel />}

      <PalangDaftar daftar={daftar} placeholder="Cari nomor PO, vendor, atau nomor PR…" testId="po-palang"
                    ekspor={{ nama: "purchase_order", kolom: KOLOM_CSV, ambilHalaman }}>
        <select value={statusFilter} data-testid="po-status-filter" aria-label="Saring status"
                onChange={(e) => daftar.setSaringan("status", e.target.value)}
                className="h-9 rounded-md border bg-background px-2 text-sm">
          <option value="">Semua status</option>
          <option value={BELUM_SELESAI}>Belum selesai</option>
          {(meta?.statuses || []).map(s =>
            <option key={s.code} value={s.code}>{s.label}</option>)}
        </select>
        <label className="flex gap-1 items-center text-sm text-muted-foreground">
          Dari
          <Input type="date" value={daftar.nilaiSaringan.dari} data-testid="po-date-from"
                 className="h-9 w-40" onChange={(e) => daftar.setSaringan("dari", e.target.value)} />
        </label>
        <label className="flex gap-1 items-center text-sm text-muted-foreground">
          sampai
          <Input type="date" value={daftar.nilaiSaringan.sampai} data-testid="po-date-to"
                 className="h-9 w-40" onChange={(e) => daftar.setSaringan("sampai", e.target.value)} />
        </label>
      </PalangDaftar>
      {statusFilter && statusFilter !== BELUM_SELESAI && (
        <p className="text-xs text-muted-foreground -mt-4" data-testid="po-status-help">
          {statusBadge(meta, statusFilter).description}
        </p>
      )}

      <Card className="overflow-hidden">
        {loading ? (
          <div className="p-4 space-y-2" data-testid="po-loading">
            {[...Array(5)].map((_, i) =>
              <div key={i} className="h-12 bg-muted rounded animate-pulse" />)}
          </div>
        ) : daftar.tampil.length === 0 ? (
          <div className="p-12 text-center text-muted-foreground text-sm space-y-2" data-testid="po-empty">
            {adaSaringan ? (
              <p>Tidak ada PO yang cocok dengan pencarian atau saringan ini. Coba hapus saringannya.</p>
            ) : (
              <>
                <p>Belum ada Purchase Order. PO lahir otomatis begitu sebuah PR disetujui — tidak dibuat manual.</p>
                <Button variant="outline" size="sm" onClick={() => navigate("/purchasing/pr")}
                        data-testid="po-empty-goto-pr">
                  Lihat Permintaan Pembelian
                </Button>
              </>
            )}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-muted/50 text-muted-foreground">
                <tr>
                  <ThUrut daftar={daftar} kunci="po_number" className="px-4 py-3 font-medium">Nomor PO</ThUrut>
                  <ThUrut daftar={daftar} kunci="po_date" className="px-4 py-3 font-medium">Tanggal</ThUrut>
                  <th className="px-4 py-3 text-left font-medium">Vendor</th>
                  <th className="px-4 py-3 text-left font-medium">Dari PR</th>
                  <th className="px-4 py-3 text-left font-medium">Wilayah</th>
                  <ThUrut daftar={daftar} kunci="total_amount" align="right" className="px-4 py-3 font-medium">Nilai</ThUrut>
                  <ThUrut daftar={daftar} kunci="status" className="px-4 py-3 font-medium">Status</ThUrut>
                </tr>
              </thead>
              <tbody className="divide-y">
                {daftar.tampil.map(po => {
                  const b = statusBadge(meta, po.status);
                  const menunggu = po.status === "PENDING_VENDOR";
                  return (
                    <tr key={po.id}
                        className={`hover:bg-muted/50 cursor-pointer ${menunggu ? "bg-warning-soft/50" : ""}`}
                        onClick={() => navigate(`/purchasing/po/${po.id}`)}
                        data-testid={`po-row-${po.po_number}`}>
                      <td className="px-4 py-3 font-medium">
                        {/* PO-LIST-NOL-TAUTAN-01 (dikoreksi sesi 17).
                            Barisnya SUDAH bisa diklik lewat onClick di <tr>.
                            Yang HILANG: cara mencapainya tanpa tetikus.
                            <tr onClick> tanpa role/tabIndex nol bisa dijangkau
                            Tab, nol merespons Enter, dan nol muncul sebagai
                            elemen interaktif di accessibility tree — itu sebab
                            audit sesi 15 menyimpulkan "daftar PO nol punya
                            tautan" padahal navigasinya ada.
                            Tombol nyata memberi keyboard + pembaca layar
                            sekaligus, dan mengikuti pola yang sudah dipakai
                            Receiving.jsx:285. */}
                        <button type="button"
                                onClick={(e) => { e.stopPropagation();
                                                  navigate(`/purchasing/po/${po.id}`); }}
                                className="text-primary underline underline-offset-2 hover:opacity-80"
                                data-testid={`po-link-${po.po_number}`}>
                          {po.po_number}
                        </button>
                      </td>
                      <td className="px-4 py-3">{formatDate(po.po_date, tzForRegion(po.region_id || po.region?.id))}</td>
                      <td className="px-4 py-3">
                        {po.vendor?.name || (
                          <span className="text-warning-soft-foreground italic">belum ditetapkan</span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-muted-foreground">
                        {po.pr?.pr_number || "—"}
                      </td>
                      <td className="px-4 py-3 text-muted-foreground">{po.region?.name || "—"}</td>
                      <td className="px-4 py-3 text-right">{formatIDR(po.total_amount)}</td>
                      <td className="px-4 py-3">
                        <StatusBadge tone={b.tone} title={b.description}>{b.label}</StatusBadge>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
        <Paginasi daftar={daftar} testId="po-paginasi" />
      </Card>
    </div>
  );
}
